import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { readContract } from '@wagmi/core';

// eslint-disable-next-line deprecate/import
import { wagmiConfig } from '@shared/common/providers/LegacyWeb3Provider/wagmi';
import { DEFAULT_TARGET_CHAIN, tokensInfo } from '@shared/constants/wallets';
import { convertTimestampSecondsToMs } from '@shared/helpers/date';
import { concatCurrencyToAmount } from '@shared/helpers/web3/formatters';
import { weiToNumber } from '@shared/helpers/web3/formatters/numberToWei';

import { formatReward } from '@ui/uikit/utils';

import { RAFFLE_CONTRACT } from '../contract';
import { getQueryKey } from './config';
import type { AmountInfo } from './types';

type RaffleRoundInfo = {
  roundId: bigint;
  prizePool: AmountInfo;
  prizePoolUsdt: AmountInfo;
  closeTimestamp: number;
  isEnded: boolean;
};

type RaffleRoundInfoResponse = RaffleRoundInfo | null;

const getLastRoundInfo = async (): Promise<RaffleRoundInfoResponse> => {
  const response = await readContract(wagmiConfig, {
    ...RAFFLE_CONTRACT,
    chainId: DEFAULT_TARGET_CHAIN.id,
    functionName: 'getLastRoundInfo',
  });

  if (!Array.isArray(response) || response.length < 2) {
    return null;
  }

  const roundId = response[0];
  const result = response[1];
  const prizeNumber = weiToNumber(result.prizePool, tokensInfo.SQR.decimals);
  const prizeUsdtNumber = weiToNumber(result.prizePoolUsdt, tokensInfo.USDT.decimals);
  const closeTimestamp = convertTimestampSecondsToMs(result.closeTimestamp);

  const isEnded = closeTimestamp - Date.now() <= 0;

  return {
    roundId,
    closeTimestamp,
    prizePool: {
      wei: result.prizePool,
      value: prizeNumber,
      formatted: concatCurrencyToAmount(formatReward(prizeNumber), tokensInfo.SQR.symbol),
    },
    prizePoolUsdt: {
      wei: result.prizePoolUsdt,
      value: prizeUsdtNumber,
      formatted: concatCurrencyToAmount(formatReward(prizeUsdtNumber), tokensInfo.USDT.symbol),
    },
    isEnded,
  };
};

const REFETCH_INTERVAL_IF_NEW_RAFFLE_NOT_CREATED_YET = convertTimestampSecondsToMs(10);

export const useLastRoundInfo = (options?: UseQueryOptions<RaffleRoundInfoResponse>) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: getQueryKey('lastRoundInfo'),
    queryFn: async () => {
      const response = await getLastRoundInfo();

      // when got not ended raffle, invalidate rounds list
      if (!response?.isEnded) {
        void queryClient.invalidateQueries({
          queryKey: getQueryKey('rounds'),
        });
      }

      return response;
    },
    refetchInterval: ({ state: { data } }) => {
      if (data && data.closeTimestamp) {
        const currentTimestamp = Date.now();
        const timeToClose = data.closeTimestamp - currentTimestamp;

        if (timeToClose <= 0) {
          return REFETCH_INTERVAL_IF_NEW_RAFFLE_NOT_CREATED_YET;
        }

        return timeToClose;
      }

      return REFETCH_INTERVAL_IF_NEW_RAFFLE_NOT_CREATED_YET;
    },
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 0,
    ...options,
  });
};
